import React, { useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Copy, Trash2, History } from "lucide-react";
import { updateTaskField } from "../../api/ProjectApi";
import { getJirItemUrl } from "../../api/api";
import { navigateTo } from "../../Navigator";

interface CardContextMenuProps {
  x: number;
  y: number;
  task: any;
  onDelete: () => void;
  onClose: () => void;
}

/** Right-click context menu for board cards */
const CardContextMenu: React.FC<CardContextMenuProps> = ({ x, y, task, onDelete, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const contentType = task.get("type") || "COMPANY";
  const buckets = useSelector((state: any) => state.bucketReducer.get(contentType));
  const currentBucketId = task.getIn(["data", "bucketId", "value"]) || "";

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleMoveToStage = useCallback(
    async (bucketId: string) => {
      await updateTaskField(task.get("id"), "bucketId", bucketId);
      onClose();
      // Trigger refresh by dispatching a search
      window.location.reload(); // Simple approach - could be improved with dispatch
    },
    [task, onClose]
  );

  const handleOpenInNewTab = useCallback(() => {
    const jiraUrl = getJirItemUrl({
      workspaceId: task.get("projectId"),
      itemId: task.get("id"),
      type: task.get("type"),
      view: "BOARD",
    });
    const appUrl = `/workspaces/${task.get("projectId")}/items/${task.get("id")}?type=${task.get("type")}&view=BOARD`;
    navigateTo({ jiraUrlParams: { relativeUrl: jiraUrl }, appUrl, navigate });
    onClose();
  }, [task, navigate, onClose]);

  const handleCopyLink = useCallback(() => {
    const url = `${window.location.origin}/workspaces/${task.get("projectId")}/items/${task.get("id")}?type=${task.get("type")}`;
    navigator.clipboard.writeText(url);
    onClose();
  }, [task, onClose]);

  // Position menu within viewport
  const menuStyle: React.CSSProperties = {
    position: "fixed",
    left: x,
    top: y,
    zIndex: 9999,
  };

  const bucketList = buckets?.toJS ? buckets.toJS() : [];

  return (
    <div ref={menuRef} style={menuStyle}>
      <div className="w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-1 text-sm">
        {/* Open */}
        <button
          onClick={handleOpenInNewTab}
          className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50"
        >
          <History className="w-4 h-4 text-gray-400" />
          <span>Open in page</span>
        </button>

        <div className="border-t border-gray-100 my-1" />

        {/* Move to stage submenu */}
        {bucketList.length > 0 && (
          <div className="group relative">
            <button className="w-full flex items-center justify-between px-3 py-2 text-gray-700 hover:bg-gray-50">
              <div className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <span>Move to stage</span>
              </div>
              <ArrowRight className="w-3 h-3 text-gray-400" />
            </button>
            <div className="hidden group-hover:block absolute left-full top-0 ml-1">
              <div className="w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1">
                {bucketList.map((bucket: any) => (
                  <button
                    key={bucket.id}
                    onClick={() => handleMoveToStage(bucket.id)}
                    disabled={bucket.id === currentBucketId}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                      bucket.id === currentBucketId
                        ? "text-blue-600 font-medium bg-blue-50"
                        : "text-gray-700"
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${
                        bucket.id === currentBucketId ? "bg-blue-500" : "bg-gray-300"
                      }`}
                    />
                    <span className="truncate">{bucket.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Copy link */}
        <button
          onClick={handleCopyLink}
          className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-50"
        >
          <Copy className="w-4 h-4 text-gray-400" />
          <span>Copy link</span>
        </button>

        <div className="border-t border-gray-100 my-1" />

        {/* Delete */}
        <button
          onClick={onDelete}
          className="w-full flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4" />
          <span>Delete</span>
        </button>
      </div>
    </div>
  );
};

export default CardContextMenu;
